import React, { useState, useCallback, useRef } from 'react';

import { useQuery } from 'react-query';
import { useNavigate } from 'react-router-dom';
import { Icon } from 'src/common/components/Icon';
import { OrbitIcons } from 'src/common/constants.';

import { fetchAccounts, InterstellarAccount } from '../api';
import { SipTrunkSummary } from '../components';

export const SipTrunks: React.FC = () => {
  const navigate = useNavigate();
  const [searchTerm, setSearchTerm] = useState('');
  const searchInputRef = useRef<HTMLInputElement>(null);

  const {
    data: accounts = [],
    isLoading,
    refetch,
  } = useQuery<InterstellarAccount[], Error>('interstellar-accounts', fetchAccounts, {
    refetchOnWindowFocus: false,
    retry: 1,
  });

  const filteredAccounts = accounts.filter((account) => {
    const q = searchTerm.toLowerCase();
    return (
      account.name.toLowerCase().includes(q) ||
      account.phone_number?.toLowerCase().includes(q) ||
      account.sip_trunk?.toLowerCase().includes(q)
    );
  });

  const handleAccountSelect = useCallback(
    (account: InterstellarAccount): void => {
      navigate(`/app/phone_settings/accounts/${account.id}`);
    },
    [navigate]
  );

  return (
    <div className="flex flex-col h-full">
      {/* Header */}
      <div className="border-b border-white-2.0 bg-primary-2.0 px-6 py-3">
        <div className="flex items-center justify-between">
          <div className="flex items-center gap-4">
            <div>
              <h1 className="text-lg font-bold text-white-0.0">SIP Trunks</h1>
              {isLoading ? (
                <p className="text-xs text-white-1.0">Cargando cuentas...</p>
              ) : (
                <p className="text-xs text-white-1.0">
                  {filteredAccounts.length} {filteredAccounts.length === 1 ? 'cuenta' : 'cuentas'}
                </p>
              )}
            </div>
          </div>

          <div className="flex items-center gap-3">
            <button
              type="button"
              onClick={() => refetch()}
              className="flex items-center gap-1.5 rounded-md bg-white/10 px-3 py-1.5 text-xs text-white-0.0 hover:bg-white/20"
            >
              <Icon name={OrbitIcons.RefreshCw} size={14} />
              Actualizar
            </button>

            <label className="group flex w-64 cursor-text items-center gap-3 rounded-full border border-white/25 bg-white-0.0 py-2.5 pl-4 pr-5 shadow-md transition-shadow focus-within:border-white/80 focus-within:shadow-lg focus-within:ring-2 focus-within:ring-white/35 sm:w-72">
              <Icon
                name={OrbitIcons.Search}
                size={18}
                className="flex-shrink-0 text-gray-3.0 transition-colors group-focus-within:text-primary-2.0"
              />
              <input
                ref={searchInputRef}
                type="search"
                enterKeyHint="search"
                autoComplete="off"
                placeholder="Buscar cuentas…"
                value={searchTerm}
                onChange={(e) => setSearchTerm(e.target.value)}
                className="min-w-0 flex-1 border-0 bg-transparent p-0 text-sm font-medium text-black-1.0 placeholder-gray-2.0 focus:outline-none focus:ring-0"
              />
            </label>
          </div>
        </div>
      </div>

      {/* Content */}
      <div className="flex flex-1 min-h-0 overflow-hidden">
        <SipTrunkSummary accounts={filteredAccounts} onAccountSelect={handleAccountSelect} />
      </div>
    </div>
  );
};
